import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  DestroyRef,
  effect,
  inject,
  Injectable,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { LayoutService } from './layout.service';
import { SettingsService } from './settings.service';

@Injectable({
  providedIn: 'root',
})
export class BreakpointService {
  private readonly layoutService = inject(LayoutService);
  private readonly settingsService = inject(SettingsService);
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);

  private readonly viewportWidth = signal(1920);

  private readonly resizeHandler = () => {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.viewportWidth.set(window.innerWidth);
  };

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.viewportWidth.set(window.innerWidth);
      window.addEventListener('resize', this.resizeHandler, { passive: true });
      this.destroyRef.onDestroy(() => {
        window.removeEventListener('resize', this.resizeHandler);
      });
    }

    effect(() => {
      const responsive = this.layoutService.layout().ResponsiveLayout;

      if (isPlatformBrowser(this.platformId)) {
        const width = this.viewportWidth();
        this.applyBreakpointClasses(
          width,
          responsive.TabletBreakpoint,
          responsive.MobileBreakpoint,
          responsive.HeaderMenuBreakpoint,
        );
        return;
      }

      const isMobile = this.settingsService.getMobileStatus();
      const isTablet = this.settingsService.getTabletStatus();
      this.applySsrBreakpointClasses(
        isMobile,
        isTablet,
        responsive.TabletBreakpoint,
        responsive.HeaderMenuBreakpoint,
      );
    });
  }

  private applySsrBreakpointClasses(
    isMobile: boolean,
    isTablet: boolean,
    tabletBreakpoint: number,
    headerMenuBreakpoint: number,
  ): void {
    const root = this.document.documentElement;
    if (!root) {
      return;
    }

    root.classList.remove('bp-mobile', 'bp-tablet', 'bp-desktop');

    if (isMobile) {
      root.classList.add('bp-mobile');
    } else if (isTablet) {
      root.classList.add('bp-tablet');
    } else {
      root.classList.add('bp-desktop');
    }

    const menuMobile =
      isMobile || (isTablet && headerMenuBreakpoint >= tabletBreakpoint);
    const menuTablet = isTablet && !menuMobile;
    root.classList.toggle('bp-menu-mobile', menuMobile);
    root.classList.toggle('bp-menu-tablet', menuTablet);
    root.classList.toggle('bp-menu-desktop', !menuMobile);
  }

  private applyBreakpointClasses(
    width: number,
    tabletBreakpoint: number,
    mobileBreakpoint: number,
    headerMenuBreakpoint: number,
  ): void {
    const root = this.document.documentElement;
    if (!root) {
      return;
    }

    root.classList.remove('bp-mobile', 'bp-tablet', 'bp-desktop');

    if (width <= mobileBreakpoint) {
      root.classList.add('bp-mobile');
    } else if (width <= tabletBreakpoint) {
      root.classList.add('bp-tablet');
    } else {
      root.classList.add('bp-desktop');
    }

    const menuMobile = width <= headerMenuBreakpoint;
    const menuTablet = !menuMobile && width <= tabletBreakpoint;
    root.classList.toggle('bp-menu-mobile', menuMobile);
    root.classList.toggle('bp-menu-tablet', menuTablet);
    root.classList.toggle('bp-menu-desktop', !menuMobile && !menuTablet);
  }
}
